import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "src/configuration";
import { useSignalR } from "src/hooks";
import {
  ActionItemSummaryDto,
  AddActionItemMetricDto,
  FetchError,
  optimisticallyUpdateQueryData,
  queryKeys,
  useAccessToken,
} from "src/http";

type Variables = {
  actionItemGuid: string;
  planGuid: string;
  metricCollectionGuid: string | null;
  metricGuid: string;
  sortOrder: string;
};

const useAddActionItemMetricRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function addActionItemMetricRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: AddActionItemMetricDto = {
      metricGuid: variables.metricGuid,
      sortOrder: variables.sortOrder,
    };

    const response = await fetch(`${configuration.apiRootUrl}/action-items/${variables.actionItemGuid}/metrics`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { addActionItemMetricRequest };
};

const useAddActionItemMetric = () => {
  const { addActionItemMetricRequest } = useAddActionItemMetricRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: addActionItemMetricRequest,
    onMutate: async (variables) => {
      const cachedSummaryData = await optimisticallyUpdateQueryData<ActionItemSummaryDto>(
        queryClient,
        queryKeys.actionItems.summary(variables.actionItemGuid),
        () => ({
          metricCount: 1,
        }),
      );

      return {
        cachedSummaryData,
      };
    },
    onSuccess: (_data, variables) => {
      signalR.sendRefetchActionItem(variables.actionItemGuid);
      signalR.sendRefetchComments(variables.actionItemGuid);
      signalR.sendRefetchPlan(variables.planGuid);
    },
    onError: (_error, variables, context) => {
      queryClient.setQueryData(queryKeys.actionItems.summary(variables.actionItemGuid), context?.cachedSummaryData);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.actionItems.item(variables.actionItemGuid));
      queryClient.invalidateQueries(queryKeys.plans.getPlan(variables.planGuid));
      queryClient.invalidateQueries(queryKeys.plans.getPlanSubitems(variables.planGuid));

      if (variables.metricCollectionGuid) {
        queryClient.invalidateQueries(queryKeys.metrics.getMetricCollection(variables.metricCollectionGuid));
      }
    },
  });
};

export { useAddActionItemMetric };
